'use client' // Error components must be Client Components

import { useEffect } from 'react'
import Link from 'next/link'
import { Sentry } from '@/lib/sentry'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Log error to console (can integrate Sentry later)
    console.error('[Error Boundary] Caught error:', error)
    Sentry.captureException(error)
  }, [error])

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      padding: '2rem',
      textAlign: 'center',
      backgroundColor: '#0a0a0a',
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{ maxWidth: '600px' }}>
        <h1 style={{
          fontSize: '2.5rem',
          marginBottom: '1rem',
          fontWeight: '600',
        }}>
          Something went wrong
        </h1>
        <p style={{
          marginBottom: '2rem',
          opacity: 0.7,
          fontSize: '1.1rem',
          lineHeight: '1.6',
        }}>
          {error.message || 'An unexpected error occurred. Please try again.'}
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => reset()}
            style={{
              padding: '0.875rem 2rem',
              fontSize: '1rem',
              backgroundColor: '#ffffff',
              color: '#000000',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'transform 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Try again
          </button>
          <Link
            href="/"
            style={{
              padding: '0.875rem 2rem',
              fontSize: '1rem',
              color: '#ffffff',
              textDecoration: 'none',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'border-color 0.2s',
              display: 'inline-block',
            }}
            onMouseOver={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)'}
            onMouseOut={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)'}
          >
            Return to homepage
          </Link>
        </div>
        {process.env.NODE_ENV === 'development' && error.stack && (
          <details style={{
            marginTop: '3rem',
            textAlign: 'left',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            padding: '1rem',
            borderRadius: '6px',
          }}>
            <summary style={{ cursor: 'pointer', fontWeight: '500', marginBottom: '0.5rem' }}>
              Error Details (Development Only)
            </summary>
            <pre style={{
              fontSize: '0.875rem',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  )
}
